<script setup>
import {ref, computed, watch, onUnmounted} from 'vue'
import {useRoute} from "vue-router"
import {useViewStore} from '@/stores/view'
import {useAppStore} from '@/stores/appStore'

// Component Imports
import OdpCourseDatesCoursesMenu from '@/bespoke/odp/OdpCourseDatesCoursesMenu'
import OdpCourseDatesSchoolsMenu from '@/bespoke/odp/OdpCourseDatesSchoolsMenu'
import ListingOptionsAddBtn from './ListingOptionsAddBtn'
import ListingOptionsArchiveBtn from './ListingOptionsArchiveBtn'
import ListingOptionsDeleteBtn from './ListingOptionsDeleteBtn'
import ListingOptionsEditBtn from './ListingOptionsEditBtn'
import ListingOptionsSearchModalBtn from './ListingOptionsSearchModalBtn'
import SearchFiltersResetBtn from './SearchFiltersResetBtn'
import ListingOptionsPaginationMetrics from './ListingOptionsPaginationMetrics'
import ListingOptionsPreviousBtn from './ListingOptionsPreviousBtn'
import ListingOptionsNextBtn from './ListingOptionsNextBtn'
import ListingOptionsMenu from './ListingOptionsMenu'
import ListView from './ListView'
import TableView from './TableView'
import GridView from './GridView'
import ShowSiteTreeBtn from '@/common/ShowSiteTreeBtn'
import ListingBreadcrumbs from "./ListingBreadcrumbs.vue"
import SearchFilterToolbar from "@/common/search-filters/SearchFilterToolbar.vue"

const props = defineProps({
    tableName: String,
})

const route = useRoute()
const appStore = useAppStore()

const viewId = ref(undefined)
const viewStore = ref(null)

const selectMode = computed(() => viewStore.value?.selectMode)
const viewMode = computed(() => viewStore.value?.viewMode)

const isOdp = computed(() => {
    return (
        (appStore.sitename || '').indexOf('outdoorsproject') === 0 &&
        props.tableName === 'odp_course_dates'
    )
})

const lastSelectedId = computed(() => {
    const selectedIds = viewStore.value?.selectedIds || []
    if (selectedIds.length > 0) {
        return selectedIds[selectedIds.length - 1]
    }
    return undefined
})

const init = () => {
    viewId.value = 'view:' + props.tableName
    viewStore.value = useViewStore(viewId.value)

    viewStore.value.init({
        listingName: props.tableName,
        query: route.query
    })

    appStore.mainViewId = viewId.value
}

init()

watch(() => props.tableName, () => {
    init()
})

onUnmounted(() => {
    if (appStore.mainViewId === viewId.value) {
        appStore.mainViewId = false
    }
})
</script>

<template>
    <section :id="tableName" class="d-flex h-100 overflow-auto flex-column">
        <header>
            <div class="d-flex border-bottom">
                <div class="flex-fill d-flex align-items-center">
                    <template v-if="!selectMode">
                        <ListingOptionsAddBtn :view-id="viewId"/>
                        <ListingOptionsArchiveBtn :view-id="viewId" class="btn rounded-0"/>
                        <ListingOptionsDeleteBtn :view-id="viewId" class="btn rounded-0"/>
                        <ListingOptionsEditBtn :view-id="viewId"/>
                    </template>
                    <ListingOptionsSearchModalBtn :view-id="viewId"/>
                    <SearchFiltersResetBtn :view-id="viewId"/>
                    <ShowSiteTreeBtn :table-name="tableName" :id="lastSelectedId" :site-tree-id="'siteTreeOffcanvas'"/>
                    <ListingBreadcrumbs :view-id="viewId"/>

                    <template v-if="isOdp" class="flex-fill">
                        <OdpCourseDatesCoursesMenu :view-id="viewId" class="ms-1"/>
                        <OdpCourseDatesSchoolsMenu :view-id="viewId" class="ms-1"/>
                    </template>
                </div>

                <div class="d-flex align-items-center">
                    <ListingOptionsPaginationMetrics :view-id="viewId" class="mx-3"/>
                    <ListingOptionsPreviousBtn :view-id="viewId"/>
                    <ListingOptionsNextBtn :view-id="viewId"/>
                    <ListingOptionsMenu :view-id="viewId"/>
                </div>
            </div>

            <SearchFilterToolbar :view-id="viewId"/>
        </header>

        <main class="listing flex-grow-1 overflow-auto pt-3">
            <div v-if="viewMode === 'table'">
                <ListView :view-id="viewId" class="list-view d-md-none mb-3"/>
                <TableView :view-id="viewId" class="table-view d-none d-md-block mb-3"/>
            </div>

            <GridView v-if="viewMode === 'thumbnail'" :view-id="viewId"/>
        </main>

        <Dump>{{ viewStore }}</Dump>
    </section>
</template>

<style scoped>
.listing {
    font-size: 85%;
}
</style>